import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { servicesNavGroups } from '../../data/navServices';
import { useLanguage } from '../../context/LanguageContext';
import './ServicesNavDropdown.css';

const getGroups = (ids) => ids.map((id) => servicesNavGroups.find((g) => g.id === id)).filter(Boolean);

/** Groupes nav — items issus de services.json et interpreting.json */
function useNavGroups(ids) {
  return getGroups(ids);
}

/** Label affiché : noms du services.json (fr/en/ar) prioritaires ; i18n en fallback */
const itemLabel = (item, menu, lang) =>
  item.names?.[lang] || item.title || menu[item.key];

const groupLabel = (group, menu, lang) =>
  group.names?.[lang] || group.title || menu[`${group.id}Group`];

/** Panneau desktop générique : une colonne par groupe */
function NavPanel({ groupIds, menu, labels, onNavigate, viewAllPath }) {
  const { lang } = useLanguage();
  const groups = useNavGroups(groupIds);

  return (
    <div className="nav-services-panel nav-services-panel--unified">
      <div
        className="nav-services-unified"
        style={{ '--nav-cols': Math.min(groups.length, 3) }}
      >
        {groups.map((group) => (
          <div key={group.id} className="nav-services-unified-col">
            <p className="nav-services-tree-title">{groupLabel(group, menu, lang)}</p>
            <ul className="nav-services-tree-links">
              {group.items.map((item) => (
                <li key={item.key}>
                  <Link to={item.path} className="nav-services-panel-link" onClick={onNavigate}>
                    {itemLabel(item, menu, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <Link to={viewAllPath} className="nav-services-panel-footer" onClick={onNavigate}>
        {labels.viewAll}
        <ChevronRight size={14} aria-hidden="true" />
      </Link>
    </div>
  );
}

/** Accordéon mobile générique */
function NavDrawer({ groupIds, menu, labels, onNavigate, viewAllPath }) {
  const { lang } = useLanguage();
  const groups = useNavGroups(groupIds);

  return (
    <div className="nav-drawer-services-accordion">
      {groups.map((group) => (
        <div key={group.id} className="nav-drawer-services-group-block">
          {groups.length > 1 && (
            <p className="nav-drawer-services-group-label">{groupLabel(group, menu, lang)}</p>
          )}
          {group.items.map((item) => (
            <Link
              key={item.key}
              to={item.path}
              className="nav-drawer-services-item"
              onClick={onNavigate}
            >
              {itemLabel(item, menu, lang)}
            </Link>
          ))}
        </div>
      ))}

      <Link to={viewAllPath} className="nav-drawer-services-view-all" onClick={onNavigate}>
        {labels.viewAll}
      </Link>
    </div>
  );
}

/* ── Traduction : professionnelle + officielle + documents ── */
export function ServicesNavDropdown({ menu, labels, onNavigate }) {
  return (
    <NavPanel
      groupIds={['professional', 'official', 'documents']}
      menu={menu}
      labels={labels}
      onNavigate={onNavigate}
      viewAllPath="/tous-les-services"
    />
  );
}

export function ServicesNavDrawer({ menu, labels, onNavigate }) {
  return (
    <NavDrawer
      groupIds={['professional', 'official', 'documents']}
      menu={menu}
      labels={labels}
      onNavigate={onNavigate}
      viewAllPath="/tous-les-services"
    />
  );
}

/* ── Interprétariat ── */
export function InterpretingNavDropdown({ menu, labels, onNavigate }) {
  return (
    <NavPanel
      groupIds={['interpreting']}
      menu={menu}
      labels={labels}
      onNavigate={onNavigate}
      viewAllPath="/services?type=interpreting"
    />
  );
}

export function InterpretingNavDrawer({ menu, labels, onNavigate }) {
  return (
    <NavDrawer
      groupIds={['interpreting']}
      menu={menu}
      labels={labels}
      onNavigate={onNavigate}
      viewAllPath="/services?type=interpreting"
    />
  );
}

/* ── Autres services ── */
export function OtherNavDropdown({ menu, labels, onNavigate }) {
  return (
    <NavPanel
      groupIds={['other']}
      menu={menu}
      labels={labels}
      onNavigate={onNavigate}
      viewAllPath="/tous-les-services"
    />
  );
}

export function OtherNavDrawer({ menu, labels, onNavigate }) {
  return (
    <NavDrawer
      groupIds={['other']}
      menu={menu}
      labels={labels}
      onNavigate={onNavigate}
      viewAllPath="/tous-les-services"
    />
  );
}
